/**
 * Pastille « Connecté » : liaison temps réel avec le boîtier.
 *
 * Indique si le boîtier a émis un battement MQTT récent (période ~5 s). Ne
 * renseigne pas la qualité des mesures : c'est la pastille « Mesures », de
 * période 30 à 60 s selon le boîtier.
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { Presence, EtatSupervision } from '../../hooks/usePresence';

type Couleur = 'success' | 'warning' | 'error' | 'default';

const RENDU: Record<string, { couleur: Couleur; texte: string; icone: React.ReactElement }> = {
  en_ligne: { couleur: 'success', texte: 'Connecté', icone: <LinkIcon /> },
  retard: { couleur: 'warning', texte: 'Connecté', icone: <LinkIcon /> },
  hors_ligne: { couleur: 'error', texte: 'Hors ligne', icone: <LinkOffIcon /> },
  indetermine: { couleur: 'default', texte: 'Lien ?', icone: <HelpOutlineIcon /> },
  inconnu: { couleur: 'default', texte: 'Lien ?', icone: <HelpOutlineIcon /> },
};

const infobulle = (p: Presence): string => {
  const age = typeof p.age_s === 'number' ? `${p.age_s.toFixed(0)} s` : null;
  switch (p.etat) {
    case 'en_ligne':
      return `Signe de vie il y a ${age ?? 'moins de 10 s'}`;
    case 'retard':
      return p.motif || `Aucun signe de vie depuis ${age}`;
    case 'hors_ligne':
      return p.motif || `Aucun signe de vie depuis ${age}`;
    case 'inconnu':
      return p.motif || "Ce boîtier n'émet pas de battement (firmware antérieur)";
    default:
      return p.motif || 'État de la liaison indéterminé';
  }
};

const PastillePresence: React.FC<{
  presence?: Presence;
  taille?: 'small' | 'medium';
  supervision?: EtatSupervision;
}> = ({ presence, taille = 'small', supervision = 'ok' }) => {
  /*
    LOT 7.4 (audit B-023) — trois situations distinctes.

    Ce composant en confondait deux :
      (a) le boîtier n'émet pas de battement (firmware antérieur)  -> rien
      (b) la supervision ne sait pas répondre (MariaDB à l'arrêt)  -> rien

    Dans les deux cas il rendait `null`, et l'absence du boîtier dans la carte
    de présence produisait exactement le même écran. Une panne de base totale
    était donc indiscernable d'un parc à jour : le tableau de bord
    n'affichait aucun signe de la panne.

    (b) a maintenant sa propre pastille, et elle est PRIORITAIRE : quand
    l'API ne répond pas, l'état du boîtier n'est pas affiché, même si une
    valeur périmée subsiste dans l'état local.
  */
  if (supervision === 'indisponible') {
    return (
      <Tooltip
        title={
          "La supervision ne peut pas joindre sa base de données. L'état de ce " +
          "boîtier est indéterminé et aucune alerte ne peut être calculée."
        }
        arrow
      >
        <Chip
          icon={<CloudOffIcon />}
          label="Supervision ?"
          color="warning"
          size={taille}
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      </Tooltip>
    );
  }

  // Aucune information : ne rien afficher plutôt qu'une pastille grise de
  // plus (boîtiers d'un firmware sans présence, chargement initial).
  if (!presence || presence.etat === 'inconnu') return null;

  const rendu = RENDU[presence.etat] || RENDU.indetermine;
  return (
    <Tooltip title={infobulle(presence)} arrow>
      <Chip
        icon={rendu.icone}
        label={rendu.texte}
        color={rendu.couleur}
        size={taille}
        variant={presence.etat === 'retard' ? 'outlined' : 'filled'}
        sx={{ fontSize: '0.75rem' }}
      />
    </Tooltip>
  );
};

export default PastillePresence;
